import { useState, useEffect } from 'react';
import type * as Leaflet from 'leaflet';
import { useServiceMap } from '../hooks/useServiceMap';
import { MapPinIcon, PhoneIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import logoImg from '../assets/optimized/2best-neighbor-home-services-06.webp';
import { useContent } from '../context/ContentContext';

interface CityData {
  name: string;
  lat: number;
  lng: number;
}

interface CountyData {
  name: string;
  color: string;
  cities: CityData[];
}

const counties: CountyData[] = [
  {
    name: 'Collin County',
    color: '#1c252c',
    cities: [
      { name: 'McKinney', lat: 33.1972, lng: -96.6397 },
      { name: 'Allen', lat: 33.1032, lng: -96.6706 },
      { name: 'Frisco', lat: 33.1507, lng: -96.8236 },
      { name: 'Plano', lat: 33.0198, lng: -96.6989 },
      { name: 'Celina', lat: 33.3248, lng: -96.7844 },
      { name: 'Anna', lat: 33.3490, lng: -96.5486 },
      { name: 'Princeton', lat: 33.1801, lng: -96.4981 },
      { name: 'Fairview', lat: 33.1579, lng: -96.6350 },
      { name: 'Lucas', lat: 33.0843, lng: -96.5769 },
      { name: 'Parker', lat: 33.0565, lng: -96.6220 },
      { name: 'Murphy', lat: 33.0151, lng: -96.6131 },
      { name: 'Wylie', lat: 33.0151, lng: -96.5389 },
      { name: 'Richardson', lat: 32.9483, lng: -96.7299 },
      { name: 'Melissa', lat: 33.2860, lng: -96.5728 },
    ],
  },
  {
    name: 'Grayson County',
    color: '#e58c32',
    cities: [
      { name: 'Sherman', lat: 33.6357, lng: -96.6089 },
      { name: 'Denison', lat: 33.7557, lng: -96.5367 },
      { name: 'Whitesboro', lat: 33.6562, lng: -96.9067 },
      { name: 'Van Alstyne', lat: 33.4215, lng: -96.5775 },
      { name: 'Howe', lat: 33.5079, lng: -96.6128 },
      { name: 'Gunter', lat: 33.4479, lng: -96.7475 },
    ],
  },
  {
    name: 'Hunt County',
    color: '#826040',
    cities: [
      { name: 'Greenville', lat: 33.1385, lng: -96.1108 },
      { name: 'Commerce', lat: 33.2468, lng: -95.8999 },
      { name: 'Quinlan', lat: 32.9104, lng: -96.1355 },
      { name: 'Caddo Mills', lat: 33.0676, lng: -96.2286 },
      { name: 'West Tawakoni', lat: 32.8937, lng: -96.0269 },
      { name: 'Campbell', lat: 33.1501, lng: -95.9499 },
      { name: 'Celeste', lat: 33.2918, lng: -96.1922 },
      { name: 'Wolfe City', lat: 33.3707, lng: -96.0686 },
    ],
  },
  {
    name: 'Dallas Area',
    color: '#8b8179',
    cities: [
      { name: 'Dallas', lat: 32.7767, lng: -96.7970 },
      { name: 'Addison', lat: 32.9612, lng: -96.8292 },
      { name: 'Carrollton', lat: 32.9537, lng: -96.8903 },
      { name: 'Farmers Branch', lat: 32.9263, lng: -96.8961 },
      { name: 'Coppell', lat: 32.9546, lng: -97.0150 },
      { name: 'The Colony', lat: 33.0807, lng: -96.8861 },
      { name: 'Little Elm', lat: 33.1626, lng: -96.9375 },
      { name: 'Sachse', lat: 32.9762, lng: -96.5953 },
      { name: 'Mesquite', lat: 32.7668, lng: -96.5992 },
    ],
  },
];

const DEFAULT_CENTER: [number, number] = [33.15, -96.5];
const DEFAULT_ZOOM = 9;

function createMarkerIcon(L: typeof Leaflet, _color: string, isSelected: boolean) {
  const size = isSelected ? 38 : 30;
  const borderColor = isSelected ? '#E8751A' : '#1c252c';
  const borderWidth = isSelected ? 3 : 2;
  const shadow = isSelected ? 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))' : 'drop-shadow(0 1px 3px rgba(0,0,0,0.2))';
  const html = `
    <div style="width:${size}px;height:${size}px;border-radius:50%;background:white;border:${borderWidth}px solid ${borderColor};display:flex;align-items:center;justify-content:center;overflow:hidden;filter:${shadow}">
      <img decoding="async" loading="lazy" src="${logoImg}" alt="" width="32" height="32" style="width:${size - 6}px;height:${size - 6}px;object-fit:contain;" />
    </div>
  `;
  return L.divIcon({
    html,
    className: 'custom-marker',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

const allCities = counties.flatMap((county) =>
    county.cities.map((city) => ({ ...city, county }))
  );

const CITIES_PER_PAGE = 6;

export default function ServiceAreas() {
  const { content, settings } = useContent();
  const sa = content.serviceAreas;
  const [selectedCity, setSelectedCity] = useState<CityData | null>(null);
  const [activeCountyIndex, setActiveCountyIndex] = useState<number | null>(null);
  const [cityPage, setCityPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const { mapRef, mapContainerRef, markersRef, leafletRef, mapReady } = useServiceMap();

  const activeCounty = activeCountyIndex !== null ? counties[activeCountyIndex] : null;
  const displayCounty = activeCounty || counties[0];
  const totalPages = Math.ceil(displayCounty.cities.length / CITIES_PER_PAGE);
  const paginatedCities = displayCounty.cities.slice(
    cityPage * CITIES_PER_PAGE,
    (cityPage + 1) * CITIES_PER_PAGE
  );



  const filteredSearchResults = searchQuery.trim().length > 0
    ? allCities.filter((c) =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.county.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  useEffect(() => {
    const map = mapRef.current;
    const markers = markersRef.current;
    const L = leafletRef.current;
    if (!map || !markers || !L) return;

    markers.clearLayers();

    const citiesToShow = activeCounty ? activeCounty.cities : allCities;
    const countyForDisplay = activeCounty || counties[0];

    citiesToShow.forEach((city) => {
      const isSelected = selectedCity?.name === city.name;
      const marker = L.marker([city.lat, city.lng], {
        title: `HVAC service in ${city.name}, TX`,
        icon: createMarkerIcon(L, countyForDisplay.color, isSelected),
      });

      const cityCountyName = 'county' in city ? (city as typeof allCities[number]).county.name : countyForDisplay.name;

      marker.bindPopup(`
        <div style="text-align:center">
          <p style="font-weight:600;margin:0 0 4px">${city.name}, TX</p>
          <p style="font-size:0.875rem;color:#666;margin:0 0 8px">${cityCountyName}</p>
          <a href="tel:9033212265" style="display:inline-block;padding:12px;background:#E8751A;color:white;font-size:0.875rem;border-radius:4px;text-decoration:none;font-weight:500">Call Now</a>
        </div>
      `);

      marker.on('click', () => {
        setSelectedCity(city);
        map.flyTo([city.lat, city.lng], 14, { duration: 0.8 });
      });

      marker.addTo(markers);
      marker.getElement()?.setAttribute('aria-label', `HVAC service in ${city.name}, TX`);
    });
  }, [activeCounty, selectedCity, mapReady, leafletRef, mapRef, markersRef]);

  const handleCityClick = (city: CityData) => {
    setSelectedCity(city);
    mapRef.current?.flyTo([city.lat, city.lng], 14, { duration: 0.8 });
  };

  const handleCountySelect = (index: number) => {
    setActiveCountyIndex(index);
    setCityPage(0);
    setSelectedCity(null);
    setSearchQuery('');
    const county = counties[index];
    const avgLat = county.cities.reduce((sum, c) => sum + c.lat, 0) / county.cities.length;
    const avgLng = county.cities.reduce((sum, c) => sum + c.lng, 0) / county.cities.length;
    mapRef.current?.flyTo([avgLat, avgLng], 10, { duration: 0.8 });
  };

  const handleReset = () => {
    setActiveCountyIndex(null);
    setCityPage(0);
    setSelectedCity(null);
    setSearchQuery('');
    mapRef.current?.flyTo(DEFAULT_CENTER, DEFAULT_ZOOM, { duration: 0.8 });
  };

  const handleSearchSelect = (city: typeof allCities[number]) => {
    const countyIndex = counties.findIndex((c) => c.name === city.county.name);
    setActiveCountyIndex(countyIndex);
    setCityPage(0);
    setSelectedCity(city);
    setSearchQuery('');
    mapRef.current?.flyTo([city.lat, city.lng], 14, { duration: 0.8 });
  };

  const nextPage = () => {
    if (cityPage < totalPages - 1) setCityPage(cityPage + 1);
  };

  const prevPage = () => {
    if (cityPage > 0) setCityPage(cityPage - 1);
  };

  return (
    <section id="service-areas" className="py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-brand-off-white to-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-brand-orange/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-brand-navy/5 rounded-full blur-3xl" />

      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 border border-brand-orange/30 rounded-lg px-4 py-2 mb-6">
            <span className="w-2 h-2 bg-brand-orange rounded-full animate-pulse" />
            <span className="text-brand-orange font-semibold text-sm uppercase tracking-wider">
              {sa.sectionLabel}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-brand-navy mb-4 sm:mb-6">
            {sa.heading}
          </h2>
          <p className="text-brand-warm-gray text-base sm:text-lg max-w-2xl mx-auto">
            {sa.description}
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="p-5 sm:p-8 pb-4 sm:pb-6">
                <p className="text-brand-orange font-semibold text-sm uppercase tracking-wider mb-3">
                  Service Areas
                </p>
                <h3 className="text-2xl font-serif font-bold text-brand-navy mb-3">
                  Proudly Serving{' '}
                  <span className="text-brand-orange">
                    {activeCounty ? activeCounty.name : 'North Texas'}
                  </span>
                </h3>
                <p className="text-brand-warm-gray text-sm leading-relaxed">
                  {activeCounty
                    ? `We provide 24/7 emergency HVAC services across ${activeCounty.cities.length} cities in ${activeCounty.name}.`
                    : 'We provide 24/7 emergency HVAC services across 4 counties in North Texas.'}
                </p>
              </div>

              <div className="px-5 sm:px-8 pb-4 sm:pb-6">
                <div className="flex flex-wrap items-center gap-2">
                  {counties.map((county, index) => (
                    <button
                      key={county.name}
                      onClick={() => handleCountySelect(index)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                        activeCountyIndex === index
                          ? 'bg-brand-navy text-white shadow-md'
                          : 'bg-brand-off-white text-brand-navy hover:bg-gray-200'
                      }`}
                    >
                      {county.name.replace(' County', '').replace(' Area', '')}
                    </button>
                  ))}
                  {activeCountyIndex !== null && (
                    <button
                      onClick={handleReset}
                      className="px-3 py-2 text-sm font-medium text-brand-orange hover:text-brand-navy transition-colors"
                    >
                      Reset
                    </button>
                  )}
                </div>
              </div>

              <div className="px-5 sm:px-8 pb-4 sm:pb-6">
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {paginatedCities.map((city) => (
                    <button
                      key={city.name}
                      onClick={() => handleCityClick(city)}
                      className={`flex items-center gap-3 p-4 rounded-xl border transition-all duration-200 text-left ${
                        selectedCity?.name === city.name
                          ? 'border-brand-orange bg-brand-orange/5 shadow-sm'
                          : 'border-gray-100 hover:border-brand-orange/50 hover:bg-brand-off-white'
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          selectedCity?.name === city.name
                            ? 'bg-brand-orange/10'
                            : 'bg-brand-off-white'
                        }`}
                      >
                        <MapPinIcon
                          className={`w-5 h-5 ${
                            selectedCity?.name === city.name
                              ? 'text-brand-orange'
                              : 'text-brand-navy/40'
                          }`}
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate text-brand-navy">
                          {city.name}
                        </p>
                        <p className="text-xs text-brand-warm-gray truncate">
                          {displayCounty.name.replace(' County', '').replace(' Area', '')} TX
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {totalPages > 1 && (
                <div className="px-5 sm:px-8 pb-4 sm:pb-6">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={prevPage}
                      disabled={cityPage === 0}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        cityPage === 0
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-brand-navy hover:bg-brand-off-white'
                      }`}
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                      Prev
                    </button>
                    <div className="flex items-center gap-1.5">
                      {Array.from({ length: totalPages }).map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setCityPage(i)}
                          aria-label={`Show city page ${i + 1}`}
                          aria-current={cityPage === i ? 'page' : undefined}
                          className="w-11 h-11 flex items-center justify-center rounded-lg"
                        >
                          <span className={`h-2 rounded-full transition-all ${cityPage === i ? 'bg-brand-orange w-6' : 'bg-gray-300 w-2'}`} />
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={nextPage}
                      disabled={cityPage >= totalPages - 1}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        cityPage >= totalPages - 1
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-brand-navy hover:bg-brand-off-white'
                      }`}
                    >
                      Next
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {selectedCity && (
                <div className="p-6 bg-brand-navy text-white">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <MapPinIcon className="w-5 h-5 text-brand-orange" />
                      <span className="font-semibold">{selectedCity.name}, TX</span>
                    </div>
                    <button
                      onClick={() => setSelectedCity(null)}
                      aria-label="Clear selected city"
                      className="p-1 hover:bg-white/10 rounded transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-white/70 text-sm mb-4">
                    Full HVAC services available in {selectedCity.name}
                  </p>
                  <a
                    href="tel:9033212265"
                    className="flex items-center justify-center gap-2 w-full bg-brand-orange text-white px-4 py-3 rounded-lg font-semibold hover:bg-brand-tan transition-colors text-sm"
                  >
                    <PhoneIcon className="w-4 h-4" />
                    Schedule Service
                  </a>
                </div>
              )}

              {!selectedCity && (
                <div className="px-5 sm:px-8 pb-5 sm:pb-8">
                  <p className="text-xs text-brand-warm-gray">
                    Do not see your area? Give us a call - we likely cover it.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative mb-5 z-10">
              <div className={`bg-white shadow-sm transition-all duration-200 ${
                searchQuery.trim().length > 0 ? 'rounded-t-2xl' : 'rounded-2xl'
              }`}>
                <div className="relative flex items-center p-2">
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-brand-orange/10 ml-1 flex-shrink-0">
                    <MagnifyingGlassIcon className="w-5 h-5 text-brand-orange" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search for a city or county"
                    placeholder="Search for a city or county..."
                    className="flex-1 px-4 py-3.5 bg-transparent text-brand-navy text-sm font-medium placeholder:text-brand-warm-gray/50 focus:outline-none"
                  />
                  {searchQuery ? (
                    <button
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear city search"
                      className="mr-2 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4 text-brand-warm-gray" />
                    </button>
                  ) : (
                    <div className="hidden sm:flex items-center gap-1.5 mr-3 px-2.5 py-1.5 bg-brand-off-white rounded-lg">
                      <MapPinIcon className="w-3.5 h-3.5 text-brand-warm-gray/60" />
                      <span className="text-xs text-[#746c63] font-medium whitespace-nowrap">
                        {allCities.length} cities
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {filteredSearchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-white rounded-b-2xl shadow-lg border-t border-gray-100 z-20">
                  <div className="p-2">
                    {filteredSearchResults.map((city) => (
                      <button
                        key={`${city.county.name}-${city.name}`}
                        onClick={() => handleSearchSelect(city)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-brand-off-white rounded-xl transition-colors text-left group"
                      >
                        <div className="w-9 h-9 rounded-lg bg-brand-orange/10 flex items-center justify-center flex-shrink-0 group-hover:bg-brand-orange/20 transition-colors">
                          <MapPinIcon className="w-4 h-4 text-brand-orange" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-brand-navy truncate">{city.name}</p>
                          <p className="text-xs text-brand-warm-gray truncate">{city.county.name}</p>
                        </div>
                        <ChevronRightIcon className="w-4 h-4 text-brand-warm-gray/40 group-hover:text-brand-orange transition-colors flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {searchQuery.trim().length > 0 && filteredSearchResults.length === 0 && (
                <div className="absolute top-full left-0 right-0 bg-white rounded-b-2xl shadow-lg border-t border-gray-100 z-20 px-6 py-8 text-center">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                    <MagnifyingGlassIcon className="w-5 h-5 text-brand-warm-gray/50" />
                  </div>
                  <p className="text-sm font-medium text-brand-navy mb-1">No results found</p>
                  <p className="text-xs text-brand-warm-gray">No cities matching "{searchQuery}" in our service areas</p>
                </div>
              )}
            </div>

            <div className="relative rounded-2xl shadow-sm overflow-hidden z-0 h-[280px] sm:h-[450px] lg:h-[600px]">
              <div ref={mapContainerRef} style={{ height: '100%', width: '100%', position: 'relative', zIndex: 0 }} />

              <div className="absolute top-4 left-14 bg-white/95 backdrop-blur-sm rounded-lg p-2 sm:p-3 z-[1000] shadow-md pointer-events-none hidden sm:block">
                <div className="flex items-center gap-2 mb-1 sm:mb-2">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: displayCounty.color }}
                  />
                  <span className="text-xs sm:text-sm font-medium text-brand-navy">
                    {activeCounty ? activeCounty.name : 'All Areas'}
                  </span>
                </div>
                <p className="text-[10px] sm:text-xs text-brand-warm-gray">
                  {activeCounty ? `${activeCounty.cities.length} cities` : `${allCities.length} cities`}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 sm:mt-16">
          <div className="bg-brand-navy rounded-2xl p-6 sm:p-8 lg:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-orange/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4" />

            <div className="relative grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 bg-white/10 rounded-lg px-3 py-1.5 mb-4">
                  <MapPinIcon className="w-4 h-4 text-brand-orange" />
                  <span className="text-white/80 text-sm font-medium">Coverage Inquiry</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-serif font-bold text-white mb-3">
                  {sa.notFoundHeading}
                </h3>
                <p className="text-white/70 leading-relaxed">
                  {sa.notFoundDescription}
                </p>
              </div>
              <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3">
                <a
                  href={`tel:${settings.phone.replace(/[^0-9]/g, '')}`}
                  className="inline-flex items-center justify-center gap-3 w-full bg-brand-orange text-white px-8 py-4 rounded-xl font-semibold hover:bg-brand-tan transition-colors duration-300 group"
                >
                  <PhoneIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span>{settings.phone}</span>
                </a>
                <a
                  href={settings.bookingUrl} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 w-full bg-white/10 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-colors duration-300 border border-white/20"
                >
                  Request a Callback
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .custom-marker {
          background: transparent;
          border: none;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 12px;
        }
        .leaflet-popup-content {
          margin: 12px 16px;
        }
        #service-areas .leaflet-top,
        #service-areas .leaflet-bottom {
          z-index: 400 !important;
        }
        #service-areas .leaflet-pane {
          z-index: 300 !important;
        }
        #service-areas .leaflet-map-pane {
          z-index: 200 !important;
        }
      `}</style>
    </section>
  );
}
